import * as THREE from "three";

// Shared looks for the fixtures that sit in cutouts (module-level: never disposed).
const STEEL = new THREE.MeshStandardMaterial({
  color: "#c9ccd1",
  metalness: 0.9,
  roughness: 0.3,
  envMapIntensity: 1,
});
const CHROME = new THREE.MeshStandardMaterial({
  color: "#dfe3e8",
  metalness: 0.96,
  roughness: 0.18,
  envMapIntensity: 1.1,
});
const GLASS = new THREE.MeshPhysicalMaterial({
  color: "#0a0a0d",
  roughness: 0.06,
  metalness: 0.1,
  clearcoat: 1,
  clearcoatRoughness: 0.04,
  envMapIntensity: 1,
});
const DRAIN = new THREE.MeshStandardMaterial({
  color: "#6b6f75",
  metalness: 0.8,
  roughness: 0.4,
});
// matte black for oven fascia, control panels and cooktop glass surround
const MATTE = new THREE.MeshStandardMaterial({
  color: "#1b1c20",
  metalness: 0.25,
  roughness: 0.7,
});
// cast-iron burner grates on a freestanding range
const CAST = new THREE.MeshStandardMaterial({
  color: "#26272b",
  metalness: 0.35,
  roughness: 0.85,
});

const HANDLE_DIR = [0, 0, Math.PI / 2];

// Gooseneck faucet: base, column, arched spout, lever handle. Points down +z.
export const Faucet = ({ y }) => (
  <group position={[0, y, 0]}>
    <mesh position={[0, 0.3, 0]} material={CHROME} castShadow>
      <cylinderGeometry args={[0.9, 1.05, 0.6, 20]} />
    </mesh>
    <mesh position={[0, 4, 0]} material={CHROME} castShadow>
      <cylinderGeometry args={[0.5, 0.55, 7, 16]} />
    </mesh>
    <mesh
      position={[0, 7.5, 3.5]}
      rotation={[0, Math.PI / 2, 0]}
      material={CHROME}
      castShadow
    >
      <torusGeometry args={[3.5, 0.42, 12, 28, Math.PI]} />
    </mesh>
    <mesh position={[0, 6.8, 7]} material={CHROME} castShadow>
      <cylinderGeometry args={[0.4, 0.46, 1.5, 12]} />
    </mesh>
    <mesh
      position={[1.3, 2.2, 0]}
      rotation={HANDLE_DIR}
      material={CHROME}
      castShadow
    >
      <cylinderGeometry args={[0.28, 0.28, 2.4, 10]} />
    </mesh>
    <mesh position={[2.5, 2.2, 0]} material={CHROME}>
      <sphereGeometry args={[0.42, 12, 10]} />
    </mesh>
  </group>
);

// Soap dispenser / side spray in the extra faucet holes
const Accessory = ({ y }) => (
  <group position={[0, y, 0]}>
    <mesh position={[0, 1.4, 0]} material={CHROME} castShadow>
      <cylinderGeometry args={[0.45, 0.6, 2.8, 12]} />
    </mesh>
    <mesh position={[0, 3, 0.4]} rotation={[0.5, 0, 0]} material={CHROME}>
      <cylinderGeometry args={[0.28, 0.28, 1.6, 10]} />
    </mesh>
  </group>
);

// Hollow stainless basin under the cutout, faucet set behind it
export const SinkFixture = ({ w, h, thick, holes = 0, dropIn = false, open = "" }) => {
  const d = 9;
  const t = 0.3;
  // a basin clipped at a seam continues on the neighbouring piece: no wall or flange on that side
  const W = w + (open === "left" || open === "right" ? 0.7 : 1.4);
  const H = h + (open === "top" || open === "bottom" ? 0.7 : 1.4);
  const ox = open === "left" ? -0.35 : open === "right" ? 0.35 : 0;
  const oz = open === "top" ? -0.35 : open === "bottom" ? 0.35 : 0;
  const n = Math.min(holes, 6);
  return (
    <group position={[ox, 0, oz]}>
      <mesh position={[0, -d + t / 2, 0]} material={STEEL} receiveShadow>
        <boxGeometry args={[W, t, H]} />
      </mesh>
      {open !== "top" && (
      <mesh
        position={[0, -d / 2, -H / 2 + t / 2]}
        material={STEEL}
        receiveShadow
      >
        <boxGeometry args={[W, d, t]} />
      </mesh>
      )}
      {open !== "bottom" && (
      <mesh
        position={[0, -d / 2, H / 2 - t / 2]}
        material={STEEL}
        receiveShadow
      >
        <boxGeometry args={[W, d, t]} />
      </mesh>
      )}
      {open !== "left" && (
      <mesh
        position={[-W / 2 + t / 2, -d / 2, 0]}
        material={STEEL}
        receiveShadow
      >
        <boxGeometry args={[t, d, H]} />
      </mesh>
      )}
      {open !== "right" && (
      <mesh
        position={[W / 2 - t / 2, -d / 2, 0]}
        material={STEEL}
        receiveShadow
      >
        <boxGeometry args={[t, d, H]} />
      </mesh>
      )}
      {!open && (
      <mesh position={[0, -d + t + 0.05, 0]} material={DRAIN}>
        <cylinderGeometry args={[1.7, 1.7, 0.1, 24]} />
      </mesh>
      )}
      {dropIn && (
        <mesh position={[0, thick + 0.12, 0]} material={STEEL} castShadow>
          <boxGeometry args={[W + 0.4, 0.25, H + 0.4]} />
        </mesh>
      )}
      {Array.from({ length: n }).map((_, k) => (
        <group key={k} position={[(k - (n - 1) / 2) * 4, 0, -h / 2 - 2.6]}>
          {k === Math.floor((n - 1) / 2) ? (
            <Faucet y={thick + (dropIn ? 0.25 : 0)} />
          ) : (
            <Accessory y={thick + (dropIn ? 0.25 : 0)} />
          )}
        </group>
      ))}
    </group>
  );
};

// Flat prints and elements that sit on the glass (module-level, never disposed)
const RING = new THREE.MeshStandardMaterial({
  color: "#9a9ea6",
  metalness: 0.55,
  roughness: 0.5,
  emissive: "#2a2b2e",
  emissiveIntensity: 0.4,
});
const GLOW = new THREE.MeshStandardMaterial({
  color: "#3a0c06",
  emissive: "#ff4a12",
  emissiveIntensity: 1.25,
  roughness: 0.5,
  transparent: true,
  opacity: 0.72,
});
const PRINT = new THREE.MeshStandardMaterial({
  color: "#b9bcc2",
  roughness: 0.6,
  emissive: "#3a3b40",
  emissiveIntensity: 0.5,
});
const TOUCH = new THREE.MeshStandardMaterial({
  color: "#1a1b20",
  roughness: 0.35,
  metalness: 0.2,
});
const LED = new THREE.MeshStandardMaterial({
  color: "#ff2a1a",
  emissive: "#ff2a1a",
  emissiveIntensity: 2,
});
const LED_OFF = new THREE.MeshStandardMaterial({
  color: "#3a3c42",
  roughness: 0.5,
});
const FLAT = [-Math.PI / 2, 0, 0];

// Radiant element: silver outer + inner rings printed on the glass, warm when on
const Radiant = ({ x, z, y, r, on }) => (
  <group position={[x, y, z]} rotation={FLAT}>
    <mesh material={RING}>
      <ringGeometry args={[r - 0.18, r, 48]} />
    </mesh>
    <mesh material={RING}>
      <ringGeometry args={[r * 0.55, r * 0.55 + 0.14, 40]} />
    </mesh>
    {on && (
      <mesh material={GLOW} position={[0, 0, -0.004]}>
        <ringGeometry args={[r * 0.2, r - 0.3, 48]} />
      </mesh>
    )}
  </group>
);

// Induction zone: thin printed circle with cross-hair ticks and a centre dot
const Induction = ({ x, z, y, r }) => (
  <group position={[x, y, z]} rotation={FLAT}>
    <mesh material={PRINT}>
      <ringGeometry args={[r - 0.1, r, 48]} />
    </mesh>
    {[0, 1, 2, 3].map((k) => {
      const a = (k * Math.PI) / 2;
      return (
        <mesh
          key={k}
          material={PRINT}
          rotation={[0, 0, a]}
          position={[Math.cos(a) * r, Math.sin(a) * r, 0]}
        >
          <planeGeometry args={[1.6, 0.12]} />
        </mesh>
      );
    })}
    <mesh material={PRINT}>
      <circleGeometry args={[0.16, 12]} />
    </mesh>
  </group>
);

// Electric cooktop: flush black glass with radiant rings (stainless trim) or induction
// cross-hairs (frameless), and a touch-control strip along the front instead of knobs
export const Cooktop = ({ w, h, thick, variant = "radiant" }) => {
  const induction = variant === "induction";
  const gt = thick + 0.32;
  const bx = w * 0.27;
  const bz = h * 0.2;
  const spots = [
    [-bx, -bz, false],
    [bx, -bz, false],
    [-bx, bz, true],
    [bx, bz, true],
  ];
  if (w >= 34) spots.push([0, 0, true]);
  const big = Math.min(4.4, h * 0.2);
  const small = big * 0.72;
  const pad = induction ? 1.6 : 0.9;
  const stripW = Math.min(w * 0.55, 16);
  const dots = 6;
  return (
    <>
      {!induction && (
        <mesh position={[0, thick + 0.1, 0]} material={STEEL} castShadow>
          <boxGeometry args={[w + 1.6, 0.22, h + 1.6]} />
        </mesh>
      )}
      <mesh position={[0, gt - 0.11, 0]} material={GLASS} castShadow>
        <boxGeometry args={[w + pad, 0.22, h + pad]} />
      </mesh>
      {spots.map(([x, z, isBig], k) =>
        induction ? (
          <Induction
            key={k}
            x={x}
            z={z}
            y={gt + 0.01}
            r={isBig ? big : small}
          />
        ) : (
          <Radiant
            key={k}
            x={x}
            z={z}
            y={gt + 0.01}
            r={isBig ? big : small}
            on={k === 2}
          />
        ),
      )}
      <group position={[0, gt + 0.008, h / 2 - 1]} rotation={FLAT}>
        <mesh material={TOUCH}>
          <planeGeometry args={[stripW, 1.5]} />
        </mesh>
        {Array.from({ length: dots }).map((_, k) => (
          <mesh
            key={k}
            material={k === 0 ? LED : LED_OFF}
            position={[(k - (dots - 1) / 2) * (stripW / dots), 0, 0.002]}
          >
            <circleGeometry args={[0.22, 12]} />
          </mesh>
        ))}
      </group>
    </>
  );
};

// One cast-iron burner grate for a freestanding range top
const Burner = ({ x, z, y, r = 3.4 }) => (
  <group position={[x, y, z]}>
    <mesh material={CAST}>
      <cylinderGeometry args={[r, r, 0.2, 24]} />
    </mesh>
    <mesh position={[0, 0.16, 0]} rotation={[Math.PI / 2, 0, 0]} material={CAST}>
      <torusGeometry args={[r * 0.66, 0.16, 8, 22]} />
    </mesh>
    <mesh position={[0, 0.2, 0]} material={CAST}>
      <cylinderGeometry args={[0.6, 0.7, 0.3, 14]} />
    </mesh>
  </group>
);

// Side-by-side refrigerator standing on the floor, backed against the marked edge.
// Local frame: x along the edge (centred), y up from the floor (0), z outward from the edge (0).
export const Fridge = ({ edgeLen = 36, width, height }) => {
  const W = width || Math.min(42, Math.max(30, edgeLen || 36));
  const D = height === "tall" ? 25 : height === "counter" ? 26 : 30;
  const H = height === "tall" ? 84 : 70;
  const gap = 0.6;
  const dw = W / 2 - gap;
  return (
    <group>
      <mesh position={[0, H / 2, D / 2]} material={STEEL} castShadow receiveShadow>
        <boxGeometry args={[W, H, D]} />
      </mesh>
      {[-1, 1].map((s) => (
        <mesh
          key={s}
          position={[s * (W / 4), H / 2, D + 0.45]}
          material={STEEL}
          castShadow
        >
          <boxGeometry args={[dw, H - 2, 1]} />
        </mesh>
      ))}
      {/* dark reveal down the middle so the two doors read apart */}
      <mesh position={[0, H / 2, D + 0.3]} material={MATTE}>
        <boxGeometry args={[gap * 1.4, H - 2, 0.5]} />
      </mesh>
      {/* two vertical handles flanking the centre gap */}
      {[-1, 1].map((s) => (
        <mesh
          key={s}
          position={[s * 2, H * 0.56, D + 1.2]}
          material={CHROME}
          castShadow
        >
          <boxGeometry args={[0.55, H * 0.44, 0.55]} />
        </mesh>
      ))}
    </group>
  );
};

// Freestanding range: stainless body to counter height, 4-burner top, oven door with
// a handle on the outward face, a control fascia, and a low backguard at the rear.
export const Range = ({ edgeLen = 30, counterH = 34.5, width }) => {
  const W = width || Math.min(36, Math.max(24, edgeLen || 30));
  const D = 26;
  const bodyH = counterH;
  const guard = 5;
  const knobs = 4;
  const step = (W - 6) / knobs;
  const bx = W * 0.24;
  const bz = D * 0.22;
  const burners = [
    [-bx, D / 2 - bz],
    [bx, D / 2 - bz],
    [-bx, D / 2 + bz],
    [bx, D / 2 + bz],
  ];
  return (
    <group>
      <mesh position={[0, bodyH / 2, D / 2]} material={STEEL} castShadow receiveShadow>
        <boxGeometry args={[W, bodyH, D]} />
      </mesh>
      {/* oven door (dark glass) + handle on the outward face */}
      <mesh position={[0, bodyH * 0.34, D + 0.2]} material={GLASS} castShadow>
        <boxGeometry args={[W - 2.5, bodyH * 0.56, 0.6]} />
      </mesh>
      <mesh position={[0, bodyH * 0.64, D + 0.85]} material={CHROME} castShadow>
        <boxGeometry args={[W - 7, 0.6, 0.6]} />
      </mesh>
      {/* control fascia with knobs */}
      <mesh position={[0, bodyH * 0.9, D + 0.15]} material={MATTE}>
        <boxGeometry args={[W - 2, bodyH * 0.16, 0.5]} />
      </mesh>
      {Array.from({ length: knobs }).map((_, k) => (
        <mesh
          key={k}
          position={[(k - (knobs - 1) / 2) * step, bodyH * 0.9, D + 0.5]}
          rotation={[Math.PI / 2, 0, 0]}
          material={CHROME}
        >
          <cylinderGeometry args={[0.5, 0.5, 0.5, 14]} />
        </mesh>
      ))}
      {/* cooktop surface + burners */}
      <mesh position={[0, bodyH + 0.25, D / 2]} material={MATTE} receiveShadow>
        <boxGeometry args={[W, 0.5, D]} />
      </mesh>
      {burners.map(([x, z], k) => (
        <Burner key={k} x={x} z={z} y={bodyH + 0.6} r={Math.min(3.6, W / 8)} />
      ))}
      {/* backguard at the rear */}
      <mesh position={[0, bodyH + guard / 2, 0.5]} material={STEEL} castShadow>
        <boxGeometry args={[W, guard, 1]} />
      </mesh>
    </group>
  );
};

// Under-counter dishwasher: stainless body to counter height, proud front panel,
// a control strip along the top and a bar handle.
export const Dishwasher = ({ edgeLen = 24, counterH = 34.5, width }) => {
  const W = width || Math.min(30, Math.max(18, edgeLen || 24));
  const D = 24;
  const H = counterH;
  return (
    <group>
      <mesh position={[0, H / 2, D / 2]} material={STEEL} castShadow receiveShadow>
        <boxGeometry args={[W, H, D]} />
      </mesh>
      <mesh position={[0, H * 0.45, D + 0.3]} material={STEEL} castShadow>
        <boxGeometry args={[W - 1.5, H * 0.78, 0.8]} />
      </mesh>
      <mesh position={[0, H * 0.93, D + 0.35]} material={MATTE}>
        <boxGeometry args={[W - 1.5, H * 0.12, 0.7]} />
      </mesh>
      <mesh position={[0, H * 0.84, D + 0.95]} material={CHROME} castShadow>
        <boxGeometry args={[W - 4, 0.6, 0.6]} />
      </mesh>
    </group>
  );
};

// Wall-mounted range hood: a stainless canopy hung above the counter with a duct
// rising behind it.
export const Hood = ({ edgeLen = 30, width }) => {
  const W = width || Math.min(48, Math.max(30, edgeLen || 30));
  const D = 20;
  const bottom = 54;
  const canopyH = 8;
  const ductH = 22;
  return (
    <group>
      <mesh
        position={[0, bottom + canopyH / 2, D / 2]}
        material={STEEL}
        castShadow
        receiveShadow
      >
        <boxGeometry args={[W, canopyH, D]} />
      </mesh>
      {/* filter underside */}
      <mesh position={[0, bottom + 0.2, D / 2]} material={MATTE}>
        <boxGeometry args={[W - 2, 0.5, D - 2]} />
      </mesh>
      {/* duct rising at the back */}
      <mesh
        position={[0, bottom + canopyH + ductH / 2, D * 0.2]}
        material={STEEL}
        castShadow
      >
        <boxGeometry args={[W * 0.42, ductH, D * 0.32]} />
      </mesh>
    </group>
  );
};
